/**
 * Combos con bloques: «2 empanadas a escoger + una bebida a escoger».
 *
 * Un combo fijo dice «lleva esto». Uno con bloques dice «lleva dos de esto y una
 * de aquello, y el cliente decide cuáles». Es lo que hace Rappi con sus grupos
 * de opciones, y es lo que pasa en el mostrador: el cliente pide el combo, y
 * recién ahí se sabe si son dos Berracas, una y una, o dos Echadas pa' lante.
 *
 * **El catálogo guarda las reglas; la línea guarda la elección.** El producto
 * dice qué bloques tiene, cuántas unidades se escogen en cada uno y qué opciones
 * hay. Cuando el vendedor arma uno, lo que escogió se copia a la línea de la
 * venta (`elecciones`), congelado: si mañana se cambia el combo, lo que se
 * vendió ayer sigue siendo lo de ayer. De esa copia salen el descuento de
 * inventario y el costo —no del catálogo—, que es lo único que sabe si fueron
 * Berracas o Echadas.
 *
 * Una línea es UNA configuración. Tres combos iguales son una línea con
 * cantidad 3; tres combos distintos son tres líneas. Así `elecciones` es una
 * lista plana y no una lista de listas, y la cantidad de la línea sigue
 * multiplicando todo como siempre.
 *
 * Todo lo de aquí es puro y se prueba aparte: ninguna regla de mínimos o máximos
 * puede vivir solo en la pantalla.
 */
/** Los límites reales de un bloque, ya normalizados. */
export function limitesDeGrupo(g) {
    const maximo = g.seleccion === 'unica' ? 1 : Math.max(1, Math.floor(g.maximo || 1));
    const minimo = g.obligatorio ? Math.max(1, Math.min(Math.floor(g.minimo || 1), maximo)) : 0;
    return { minimo, maximo };
}
export function opcionesActivas(g) {
    return g.opciones.filter((o) => o.activa !== false);
}
/** Cuántas unidades de una opción caben en un combo. */
export function topeDeOpcion(g, o) {
    const { maximo } = limitesDeGrupo(g);
    const propio = o.maximo && o.maximo > 0 ? Math.floor(o.maximo) : maximo;
    return Math.max(0, Math.min(propio, maximo));
}
/** ¿Este combo pide escoger algo? */
export function tieneBloques(p) {
    return (p?.grupos?.length ?? 0) > 0;
}
/** El total escogido en un bloque. */
export function totalDelGrupo(elecciones, grupoId) {
    return elecciones.filter((e) => e.grupo_id === grupoId).reduce((s, e) => s + e.cantidad, 0);
}
/**
 * Lo que está mal en una elección. Vacío = se puede vender.
 *
 * Cada mensaje dice qué bloque y cuánto falta o sobra, porque lo lee un cajero
 * con la fila esperando.
 */
export function validarElecciones(grupos, elecciones) {
    const errores = [];
    const porId = new Map(grupos.map((g) => [g.id, g]));
    for (const e of elecciones) {
        const g = porId.get(e.grupo_id);
        if (!g) {
            errores.push(`«${e.nombre}» no pertenece a este combo.`);
            continue;
        }
        const o = g.opciones.find((x) => x.codigo === e.codigo);
        if (!o || o.activa === false) {
            errores.push(`«${e.nombre}» ya no está disponible en ${g.nombre}.`);
            continue;
        }
        if (e.cantidad > topeDeOpcion(g, o)) {
            errores.push(`${g.nombre}: máximo ${topeDeOpcion(g, o)} de «${o.nombre}».`);
        }
    }
    for (const g of grupos) {
        const { minimo, maximo } = limitesDeGrupo(g);
        const n = totalDelGrupo(elecciones, g.id);
        if (n < minimo) {
            const falta = minimo - n;
            errores.push(`${g.nombre}: falta${falta === 1 ? '' : 'n'} ${falta} por escoger.`);
        }
        else if (n > maximo) {
            errores.push(`${g.nombre}: máximo ${maximo}, llevas ${n}.`);
        }
    }
    return errores;
}
/** Lo que suman los recargos de lo escogido, por UN combo. */
export function recargoDeElecciones(elecciones) {
    return Math.round((elecciones ?? []).reduce((s, e) => s + (e.recargo ?? 0) * e.cantidad, 0));
}
/**
 * Una llave estable de lo escogido. Dos líneas con la misma llave son la misma
 * configuración y se pueden juntar sumando cantidad; con llaves distintas
 * tienen que quedar separadas, porque no consumen lo mismo.
 */
export function claveDeElecciones(elecciones) {
    return [...(elecciones ?? [])]
        .filter((e) => e.cantidad > 0)
        .sort((a, b) => (a.grupo_id + a.codigo).localeCompare(b.grupo_id + b.codigo))
        .map((e) => `${e.grupo_id}:${e.codigo}x${e.cantidad}`)
        .join('|');
}
/** «2 × La Berraca Papa - Carne · 1 × Café». Para la línea, la comanda y el recibo. */
export function textoDeElecciones(elecciones) {
    return (elecciones ?? [])
        .filter((e) => e.cantidad > 0)
        .map((e) => `${e.cantidad} × ${e.nombre}`)
        .join(' · ');
}
/**
 * Lo que está mal en la configuración de los bloques. Es lo que se le dice a
 * quien arma el combo, para que no se publique uno que no se puede vender.
 */
export function problemasDeGrupos(grupos) {
    const out = [];
    const ids = new Set();
    grupos.forEach((g, i) => {
        const nombre = g.nombre.trim() || `Bloque ${i + 1}`;
        if (!g.nombre.trim())
            out.push(`${nombre}: ponle un nombre.`);
        if (ids.has(g.id))
            out.push(`${nombre}: el bloque está repetido.`);
        ids.add(g.id);
        const activas = opcionesActivas(g);
        if (activas.length === 0) {
            out.push(`${nombre}: agrega al menos una opción.`);
            return;
        }
        const codigos = new Set();
        for (const o of g.opciones) {
            if (codigos.has(o.codigo))
                out.push(`${nombre}: «${o.nombre}» está dos veces.`);
            codigos.add(o.codigo);
            if (o.recargo !== undefined && o.recargo < 0)
                out.push(`${nombre}: el recargo de «${o.nombre}» no puede ser negativo.`);
        }
        const { minimo, maximo } = limitesDeGrupo(g);
        if (g.seleccion === 'multiple' && g.obligatorio && g.minimo > g.maximo) {
            out.push(`${nombre}: el mínimo (${g.minimo}) no puede ser mayor que el máximo (${g.maximo}).`);
        }
        // Lo que de verdad se puede llevar: cada opción hasta su tope.
        const capacidad = activas.reduce((s, o) => s + topeDeOpcion(g, o), 0);
        if (capacidad < minimo) {
            out.push(`${nombre}: piden ${minimo} pero las opciones solo dan para ${capacidad}.`);
        }
    });
    return out;
}
/** Un id corto para un bloque nuevo, único dentro del combo. */
export function idDeGrupoNuevo(existentes) {
    let n = existentes.length + 1;
    const usados = new Set(existentes.map((g) => g.id));
    while (usados.has(`g${n}`))
        n += 1;
    return `g${n}`;
}
/**
 * Lo que una línea de venta trae de una configuración: el precio con recargos,
 * lo escogido, y su resumen para leerlo en la línea, la comanda y el recibo.
 *
 * El recargo va **dentro del precio de la línea** y no como una línea aparte:
 * la factura sale por un solo valor, el del combo con lo que se escogió.
 * El precio de lista también lo lleva, para que un descuento se calcule sobre
 * lo que el cliente de verdad iba a pagar y no sobre el precio base.
 */
export function camposDeEleccion(precioBase, elecciones) {
    const limpias = elecciones.filter((e) => e.cantidad > 0);
    const precio = Math.round(precioBase + recargoDeElecciones(limpias));
    return {
        precio_unitario: precio,
        precio_lista: precio,
        descripcion: textoDeElecciones(limpias),
        elecciones: limpias,
    };
}
/** ¿Esta línea es la misma configuración que se acaba de armar? Si sí, se suma cantidad. */
export function mismaConfiguracion(linea, codigo, elecciones) {
    return linea.codigo === codigo && claveDeElecciones(linea.elecciones) === claveDeElecciones(elecciones);
}
